
import { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const testimonials = [
  {
    id: 1,
    content: "Benchr Intelligence gives us an in-depth view of our competitors' products and pricing. Thanks to their accurate data, we were able to revise our pricing strategy and increase our sales by 23% in just two quarters."
  },
  {
    id: 2,
    content: "The detailed market trend analysis provided by Benchr allowed us to anticipate developments in our sector. We were able to launch new products at the ideal time and gain a significant advantage over our competitors."
  },
  {
    id: 3,
    content: "Before Benchr, we spent hours manually compiling data on our competitors. Now, we have access to detailed and objective analyses of our competitors' traffic, prices, and strategies with just a few clicks."
  },
  {
    id: 4,
    content: "Benchr Intelligence's platform gives us a complete view of our e-commerce ecosystem. We can now make decisions based on objective data rather than intuition, which has transformed our business approach."
  },
  {
    id: 5,
    content: "Thanks to the actionable insights provided by Benchr, we were able to quickly adjust our strategy in response to market changes. It's like having a complete competitive intelligence department integrated into our team."
  }
];

const Testimonials = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const testimonialRef = useRef<HTMLDivElement>(null);

  const goToTestimonial = (index: number) => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setActiveIndex(index);
    
    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };

  const nextTestimonial = () => {
    goToTestimonial((activeIndex + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    goToTestimonial((activeIndex - 1 + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      nextTestimonial();
    }, 8000);

    return () => clearInterval(intervalId);
  }, [activeIndex]);

  return (
    <section 
      id="testimonials" 
      className="py-24 relative overflow-hidden bg-benchr-50 dark:bg-benchr-900/30"
    >
      {/* Background elements */}
      <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-benchr-300/10 rounded-full blur-3xl -z-10" />
      <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-benchr-500/5 rounded-full blur-3xl -z-10" />

      <div className="container max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-benchr-900 dark:text-white mb-4 tracking-tight animate-slide-up">
            Trusted by Fortune Global 500
          </h2>
          <p className="text-lg text-benchr-600 dark:text-benchr-300 animate-slide-up" style={{ animationDelay: "100ms" }}>
            See what our customers are saying about how Benchr has helped them optimize their applications.
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {/* Navigation arrows - Adjusted positioning to be closer to the block */}
          <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 flex justify-between px-0 md:px-0 lg:px-0 z-20 pointer-events-none" style={{ width: "115%", left: "-7.5%" }}>
            <button
              onClick={prevTestimonial}
              className="w-10 h-10 rounded-full bg-white dark:bg-benchr-800 shadow-md flex items-center justify-center text-benchr-700 dark:text-benchr-200 hover:bg-benchr-50 dark:hover:bg-benchr-700 transition-colors pointer-events-auto"
              disabled={isAnimating}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={nextTestimonial}
              className="w-10 h-10 rounded-full bg-white dark:bg-benchr-800 shadow-md flex items-center justify-center text-benchr-700 dark:text-benchr-200 hover:bg-benchr-50 dark:hover:bg-benchr-700 transition-colors pointer-events-auto"
              disabled={isAnimating}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Testimonial slider - Without quote icon */}
          <div
            ref={testimonialRef}
            className="relative bg-white dark:bg-benchr-800/40 rounded-2xl shadow-lg border border-benchr-100 dark:border-benchr-700/40 p-8 md:p-12 overflow-hidden min-h-[240px] mx-16 md:mx-24 lg:mx-32"
          >
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className={cn(
                  "transition-opacity duration-500 absolute inset-0 p-8 md:p-12 flex flex-col",
                  activeIndex === index ? "opacity-100 z-10" : "opacity-0 z-0"
                )}
              >
                <blockquote className="text-xl md:text-2xl text-benchr-800 dark:text-white font-medium leading-relaxed mb-6 max-w-4xl">
                  "{testimonial.content}"
                </blockquote>
              </div>
            ))}
          </div>

          {/* Indicators */}
          <div className="flex justify-center mt-8 gap-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToTestimonial(index)}
                className={cn(
                  "w-2.5 h-2.5 rounded-full transition-all duration-300",
                  activeIndex === index
                    ? "bg-benchr-700 dark:bg-benchr-400 w-8"
                    : "bg-benchr-300 dark:bg-benchr-700 hover:bg-benchr-400 dark:hover:bg-benchr-600"
                )}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
